import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  getPublicReactionPage,
  type PublicReactionPage as Reaction,
  type PublicReactionParticipant
} from '@peruselab/api-client/reactions';
import { FormulaText } from '../../components/FormulaText';
import { Icon, type IconName } from '../../components/Icon';
import { ReactionPicture, useReactionPicture } from '../../components/panels/ReactionPicture';
import { PublicLayout } from './PublicLayout';
import { SignInPrompt } from './SignInPrompt';
import { moleculePagePath, usePublicAccess } from './publicAccess';
import { CatalogueMeta } from './CatalogueMeta';
import { reactionHead } from './scientificMeta';

type PageState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly value: Reaction }
  | { readonly status: 'notFound' };

/** The actions only signed-in users can take; signed out, each asks them to sign in (#632). */
type GatedAction = 'addReactionToPaper' | 'shareReaction';

const COPIED_FEEDBACK_MS = 2000;

/** A number as a scheme writes it: whole, or to one decimal. */
function numberText(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Math.round(value * 10) / 10);
}

/** "2.5 h", "90 min", "45 s" -- a reaction time as a scheme writes it (the server's words too). */
function durationText(seconds: number): string {
  if (seconds >= 3600) return `${numberText(seconds / 3600)} h`;
  if (seconds >= 60) return `${numberText(seconds / 60)} min`;
  return `${numberText(seconds)} s`;
}

/**
 * #1124 (ADR 0023): one public reaction at `/r/<id>` -- its scheme as search draws it, then each
 * participant (linked to its page when that molecule is public, D1) and the conditions. The words
 * are the server's (`/api/reactions/<id>/page`), so the page reads the same before and after the
 * app starts. Adding it to a paper and sharing it need signing in, as for a drawing (#632).
 */
export function PublicReactionPage() {
  const { t, i18n } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { signedIn, signIn, signUp } = usePublicAccess();
  const [state, setState] = useState<PageState>({ status: 'loading' });
  const [prompt, setPrompt] = useState<GatedAction | null>(null);
  const [copied, setCopied] = useState(false);
  const drawn = useReactionPicture(id);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    getPublicReactionPage(id)
      .then((value) => {
        if (!cancelled) setState({ status: 'ready', value });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'notFound' });
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (!copied) return;
    const timer = globalThis.setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    return () => globalThis.clearTimeout(timer);
  }, [copied]);

  const run = async (action: GatedAction) => {
    if (!signedIn) {
      setPrompt(action);
      return;
    }
    if (action === 'addReactionToPaper') {
      navigate(`/draw?addReaction=${encodeURIComponent(id)}`);
      return;
    }
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const gatedButtons: readonly { readonly action: GatedAction; readonly icon: IconName; readonly label: string }[] = [
    { action: 'addReactionToPaper', icon: 'add', label: t('publicPages.addToPaper') },
    { action: 'shareReaction', icon: 'link', label: copied ? t('publicPages.linkCopied') : t('publicPages.share') }
  ];

  const participantList = (heading: string, participants: readonly PublicReactionParticipant[]) =>
    participants.length === 0 ? null : (
      <section className="public-reaction-participants">
        <h2>{heading}</h2>
        <ul>
          {participants.map((participant, index) => {
            // A private molecule goes by its formula (#1073: true subscripts), never its name (D1).
            const named = participant.name ? (
              participant.name
            ) : participant.molecularFormula ? (
              <FormulaText formula={participant.molecularFormula} />
            ) : (
              t('publicPages.reaction.structure')
            );
            const shown =
              participant.coefficient && participant.coefficient > 1 ? (
                <>
                  {participant.coefficient} {named}
                </>
              ) : (
                named
              );
            return (
              <li key={index}>
                {participant.moleculeId ? (
                  <Link to={moleculePagePath(participant.moleculeId, signedIn)}>{shown}</Link>
                ) : (
                  <span>{shown}</span>
                )}
                {participant.smiles ? <code>{participant.smiles}</code> : null}
              </li>
            );
          })}
        </ul>
      </section>
    );

  return (
    <PublicLayout>
      <Link to="/public" className="public-back">
        <Icon name="arrowBack" />
        <span>{t('publicPages.back')}</span>
      </Link>
      {state.status === 'loading' ? <p role="status">{t('common.loading')}</p> : null}
      {state.status === 'notFound' ? (
        <p className="import-error" role="alert">
          {t('publicPages.reaction.notFound')}
        </p>
      ) : null}
      {state.status === 'ready' ? (
        <article className="public-drawing public-reaction" aria-labelledby="public-reaction-title">
          <CatalogueMeta head={reactionHead(t, state.value)} />
          <header>
            <h1 id="public-reaction-title">{state.value.label}</h1>
            <p className="public-drawing-meta">
              {t('publicPages.byAuthor', { author: state.value.authorName || t('publicPages.unknownAuthor') })}
              {' · '}
              {t('publicPages.updated', {
                date: new Date(state.value.updatedAt).toLocaleDateString(i18n.language, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })
              })}
            </p>
          </header>
          <figure className="public-reaction-figure" aria-label={t('publicPages.reaction.scheme')}>
            <ReactionPicture state={drawn} />
          </figure>
          {participantList(
            t('publicPages.reaction.reactants'),
            state.value.participants.filter((p) => p.role === 'reactant')
          )}
          {participantList(
            t('publicPages.reaction.agents'),
            state.value.participants.filter((p) => p.role !== 'reactant' && p.role !== 'product')
          )}
          {participantList(
            t('publicPages.reaction.products'),
            state.value.participants.filter((p) => p.role === 'product')
          )}
          <Conditions conditions={state.value.conditions} />
          <div className="public-drawing-actions">
            {gatedButtons.map(({ action, icon, label }) => (
              <button
                key={action}
                type="button"
                className="icon-button"
                onClick={() => void run(action)}
                aria-label={label}
                title={label}
              >
                <Icon name={icon} />
                {signedIn ? null : <Icon name="lock" size="0.95rem" />}
              </button>
            ))}
          </div>
        </article>
      ) : null}
      {prompt ? (
        <SignInPrompt
          message={t(`publicPages.prompt.${prompt}`)}
          onSignIn={() => signIn(location.pathname + location.search)}
          onSignUp={() => signUp(location.pathname + location.search)}
          onClose={() => setPrompt(null)}
        />
      ) : null}
    </PublicLayout>
  );
}

function Conditions({ conditions }: { readonly conditions: Reaction['conditions'] }) {
  const { t } = useTranslation();
  const rows = [
    conditions.temperatureC !== null ? ['temperature', `${numberText(conditions.temperatureC)} °C`] : null,
    conditions.pressureAtm !== null ? ['pressure', `${numberText(conditions.pressureAtm)} atm`] : null,
    conditions.durationS !== null ? ['time', durationText(conditions.durationS)] : null,
    conditions.yieldPercent !== null ? ['yield', `${numberText(conditions.yieldPercent)} %`] : null,
    conditions.notes !== null ? ['notes', conditions.notes] : null
  ].filter((row): row is [string, string] => row !== null);
  if (rows.length === 0) return null;
  return (
    <section className="public-reaction-conditions">
      <h2>{t('publicPages.reaction.conditions')}</h2>
      <dl>
        {rows.map(([key, value]) => (
          <div key={key}>
            <dt>{t(`publicPages.reaction.${key}`)}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
